import { supabase } from './supabase';
import { VENDOR_CATALOG_COLUMNS } from './masterDataTemplates';
import { addUnit } from './taxonomy';
import {
  makeHeaderMatcher,
  textCell,
  boolCell,
  moneyCell,
  intCell,
} from './importCore';

// Issue #422, Session 78s.1. The vendor's OWN catalog — what each supplier
// sells, and what they charge for it.
//
// In plain terms: Jennifer gets a price list from Grimco — four hundred rows of
// part numbers, descriptions and prices. Most of those products D&R does not
// stock and may never stock. She loads the whole list anyway, so that the next
// time a manager asks for one of them the part number and the price are already
// there instead of being typed in under time pressure.
//
// Approved gate: docs/gates/Stede_MasterDataImport_Gate_422.html. Decision
// numbers below are that gate's.
//
// WHY THIS IS A DIFFERENT MODULE FROM vendorCatalogImport.js, which is the
// question anybody reading both will ask. That one imports the VENDOR LIST —
// who D&R buys from, one row per company. This one imports a vendor's PRODUCTS
// — many rows per company. They share the parser, the column matcher and the
// cell readers (importCore.js) and share the screen (M22's descriptor), and
// they share nothing else: different table, different key, different rules.
//
// M2 — the decision the whole module rests on. A row here writes `vendor_items`
// with `item_id` NULL and creates no inventory item. Fishbowl and Coupa draw
// the same line; NetSuite and Limble fold vendor lines into the item row and
// both document a way to lose data doing it.

// ── The header vocabulary ────────────────────────────────────────────────────
//
// Stede's own template headers come first in each list, so a file built from
// the template matches at rank 0 and nothing else can outrank it. After those
// are the words a real supplier price list uses, because the second most likely
// file after our own template is one the vendor emailed.
//
// 'sku' is DELIBERATELY not an alias for the vendor's part number, though half
// the price lists in the world label it that way. In this file SKU means D&R's
// SKU — the optional link to our catalog (M4) — and letting it also mean the
// vendor's part number would silently swap the two columns on exactly the files
// where getting it wrong matters most. An unrecognized heading is asked about;
// a heading matched to the wrong field is not.
const ALIASES = {
  vendor_id: ['vendor name', 'vendor', 'supplier', 'supplier name', 'manufacturer'],
  // WRITTEN IN NORMALISED FORM. Headings are lowercased and stripped of
  // punctuation before they are compared, so an alias containing a '#' or a '.'
  // can never match anything — "part #" arrives here as "part". Aliases that
  // carry punctuation are not wrong so much as unreachable, which is worse,
  // because nothing fails.
  vendor_item_number: [
    'vendor part number', 'vendor part', 'vendor part no', 'vendor partno',
    'part number', 'part no', 'partno', 'part',
    'catalog number', 'catalog no', 'catalogue number',
    'mfg part number', 'manufacturer part number', 'vendor item number', 'vendor item',
  ],
  off_catalog_description: ['product description', 'description', 'product', 'product name', 'item description'],
  item_id: ['item sku', 'stede sku', 'sku', 'our sku', 'stede item'],
  vendor_price: ['price per pack', 'pack price', 'price', 'list price', 'cost', 'unit price', 'case price'],
  pack_qty: ['units per pack', 'pack qty', 'pack quantity', 'qty per pack', 'quantity per pack', 'pack size'],
  pack_unit: ['package type', 'pack unit', 'packaging', 'uom', 'unit of measure', 'sold by'],
  is_preferred: ['preferred vendor', 'preferred', 'is preferred'],
};

const matcher = makeHeaderMatcher(ALIASES);

export const findHeaderRow = matcher.findHeaderRow;

// The three columns without which there is nothing to import: whose catalog it
// is, what the product is called in that catalog, and what it costs. M3 and
// M11. The rest can be absent and the import still means something.
export const REQUIRED_KEYS = ['vendor_id', 'vendor_item_number', 'vendor_price'];

export function mapHeaders(headerCells) {
  const info = matcher.mapHeaders(headerCells);
  return {
    ...info,
    missingRequired: REQUIRED_KEYS.filter(k => !info.found.has(k)),
  };
}

const LABEL = Object.fromEntries(VENDOR_CATALOG_COLUMNS.map(c => [c.key, c.header]));

// A vendor's part number identifies a product only within that vendor, and the
// database's uniqueness index compares it case-insensitively. Every lookup in
// this file goes through this function so that no single one of them can be the
// one that forgets — which is the failure the migration header warns about: a
// case-sensitive lookup finds nothing, decides the row is new, attempts an
// insert, and has the database refuse it. A quiet update becomes a hard error.
// The separator is '::' rather than a control character. An earlier draft used
// a literal NUL, which is legal JavaScript and made git treat this whole file as
// BINARY — no diff, no review, and protocol item 7 says show the diff before
// committing. '::' is safe here because the first segment is always a numeric
// id, so no part number can be crafted that collides with another vendor's key.
function partKey(vendorId, partNumber) {
  return `${vendorId}::part::${String(partNumber || '').trim().toLowerCase()}`;
}

function itemKey(vendorId, itemId) {
  return `${vendorId}::item::${itemId}`;
}

function norm(v) {
  return String(v ?? '').trim().toLowerCase();
}

// ── Reading one row ──────────────────────────────────────────────────────────
//
// Split out because both passes below need it: the pass that collects unknown
// units for the mapping step, and the pass that builds the plan. Two readers
// would be two chances to disagree about what a cell says.
function readRow(cells, mapping) {
  const supplied = {};
  const errors = [];
  // WHICH fields were supplied but unreadable, as distinct from not supplied at
  // all. Without this a rejected cell fires TWICE and the second message
  // contradicts the first: a price of -5.00 said "a price cannot be negative"
  // and then "every row needs a price", on the same row, about the same cell.
  // Found in the live test at 78s.1 — the unit tests asserted the mode and
  // never read the sentence.
  const rejected = new Set();

  for (const [indexStr, key] of Object.entries(mapping)) {
    const raw = cells[Number(indexStr)];
    let value;

    if (key === 'vendor_price') value = moneyCell(raw);
    else if (key === 'pack_qty') value = intCell(raw, { min: 1 });
    else if (key === 'is_preferred') value = boolCell(raw);
    else value = textCell(raw);

    if (value && typeof value === 'object' && value.error) {
      errors.push(`${LABEL[key] || key}: ${value.error}`);
      rejected.add(key);
    } else if (value !== undefined) {
      supplied[key] = value;
    }
  }

  return { supplied, errors, rejected };
}

// ── #468 layer 2 · an unrecognized value is a decision, not a rejection ───────
//
// M7. Rather than "row 47 is bad": "12 rows say sq ft. That is not one of our
// package types — is it square foot?" One answer fixes twelve rows. This is
// SAP's value mapping, in the same shape as the column-matching step already
// uses, applied to values instead of headings.
//
// M6 is the layer beneath it: the value is checked against the LIVE list read
// at import time, so unlike a drop-down baked into a downloaded file it cannot
// go stale. `vendor_items.pack_unit` also carries a real foreign key into
// `units_of_measure(name)` (added Session 78n2), so an unrecognized package
// type is refused by the database whether or not this check runs — which makes
// this a better error message rather than the only guard, and that is the right
// order for those two things to be in.
//
// Returns one question per distinct unknown value, never one per row. The rows
// are carried along so the question can say how many it fixes and the person
// can go and look at them.
export function collectUnknownUnits({ rows, headerRowIndex, mapping, units }) {
  const known = new Set((units || []).map(u => norm(u.name)));
  const seen = new Map();

  for (let r = headerRowIndex + 1; r < rows.length; r += 1) {
    const cells = rows[r];
    if (cells.every(c => String(c ?? '').trim() === '')) continue;
    const { supplied } = readRow(cells, mapping);
    const raw = supplied.pack_unit;
    if (raw === undefined) continue;
    if (known.has(norm(raw))) continue;

    const k = norm(raw);
    if (!seen.has(k)) seen.set(k, { field: 'pack_unit', label: LABEL.pack_unit, value: String(raw).trim(), lines: [] });
    seen.get(k).lines.push(r + 1);
  }

  return [...seen.values()].map(q => ({ ...q, count: q.lines.length }));
}

// ── The plan ─────────────────────────────────────────────────────────────────
//
// Everything the preview shows, computed before a single write.
//
// Nothing here queries. `vendors`, `items`, `vendorItems` and `units` are read
// once by the screen and passed in, so this function is pure and every rule
// below is testable without a database.
//
// `unitMap` carries the answers to collectUnknownUnits' questions:
//   'sq ft' -> 'square foot'   use this existing unit instead
//   'sq ft' -> CREATE_UNIT     add it as a new package type (M8)
//   'sq ft' -> ''              leave the package type blank on those rows
// A sentinel that no unit could ever be called, spelled so that it survives
// being read out of a <select> and written into a plain object. It must not be
// a control character: see partKey above.
export const CREATE_UNIT = '__create__';

export function buildVendorItemsPlan({
  rows, headerRowIndex, mapping,
  vendors, items, vendorItems, units, unitMap = {},
}) {
  const vendorByName = new Map();
  for (const v of vendors || []) {
    const k = norm(v.name);
    if (!vendorByName.has(k)) vendorByName.set(k, v);
  }

  const itemBySku = new Map();
  for (const it of items || []) {
    const k = norm(it.sku);
    if (!k) continue;
    if (!itemBySku.has(k)) itemBySku.set(k, it);
  }

  const knownUnits = new Set((units || []).map(u => norm(u.name)));

  // Existing rows, indexed the two ways a row can be found.
  //
  // A PROVISIONAL row is deliberately excluded from the part-number index, and
  // this is a decision rather than an oversight. A provisional row records that
  // D&R bought this thing off-catalog once — it is history, and its price is
  // what was actually paid on that order. Overwriting it with a list price
  // would rewrite the record of a purchase. The database agrees: the unique
  // index covers `is_provisional = false` only, so a separate catalog row for
  // the same part number is legal. The row gets a warning so it is visible
  // rather than surprising.
  const byPart = new Map();
  const byItem = new Map();
  const provisionalByPart = new Map();
  for (const vi of vendorItems || []) {
    if (vi.item_id != null) byItem.set(itemKey(vi.vendor_id, vi.item_id), vi);
    if (vi.vendor_item_number) {
      const k = partKey(vi.vendor_id, vi.vendor_item_number);
      if (vi.is_provisional) {
        if (!provisionalByPart.has(k)) provisionalByPart.set(k, vi);
      } else if (!byPart.has(k)) {
        byPart.set(k, vi);
      }
    }
  }

  // Who is already the preferred vendor for each item, so a row claiming
  // preferred can say whose place it is taking.
  const preferredByItem = new Map();
  for (const vi of vendorItems || []) {
    if (vi.is_preferred && vi.item_id != null) preferredByItem.set(String(vi.item_id), vi);
  }

  const plan = [];
  const seenParts = new Map();          // vendor+part -> line, M3's within-file duplicate
  const seenPreferredItems = new Map(); // item -> line, M10
  const claimedItems = new Map();       // vendor+item -> line, the UNIQUE(vendor,item) guard

  for (let r = headerRowIndex + 1; r < rows.length; r += 1) {
    const cells = rows[r];
    if (cells.every(c => String(c ?? '').trim() === '')) continue;

    const lineNo = r + 1;
    const entry = { lineNo, errors: [], warnings: [], changes: [], values: {} };
    const { supplied, errors, rejected } = readRow(cells, mapping);
    entry.errors.push(...errors);

    // ── Whose catalog is this row in. Required, and it must already exist:
    // this import prices a vendor's products, it does not invent vendors. The
    // message says which import does, because "unknown vendor" with no way
    // forward is the shape of error that produces a phone call.
    let vendor = null;
    if (supplied.vendor_id === undefined) {
      entry.errors.push('Every row needs a vendor name.');
    } else {
      vendor = vendorByName.get(norm(supplied.vendor_id)) || null;
      if (!vendor) {
        entry.errors.push(`“${supplied.vendor_id}” is not a vendor in Stede. Add the vendor first, or import the vendor list, then import these prices.`);
      } else if (vendor.is_active === false) {
        entry.warnings.push(`${vendor.name} is deactivated. The prices will load, but the vendor will not be offered when ordering until it is reactivated.`);
      }
    }

    // ── M3. The part number is the key, and a blank one is a REFUSAL rather
    // than a skip. Fishbowl silently skips these, which is the most dangerous
    // behavior found anywhere in the research: you believe four hundred rows
    // loaded and some number fewer did.
    const part = supplied.vendor_item_number;
    if (part === undefined) {
      entry.errors.push('Every row needs the vendor’s part number — it is how Stede tells one of their products from another.');
    }

    // ── M4. The Stede SKU is optional; a wrong one is an error.
    let item = null;
    if (supplied.item_id !== undefined) {
      item = itemBySku.get(norm(supplied.item_id)) || null;
      if (!item) {
        entry.errors.push(`No item in our catalog has the SKU “${supplied.item_id}”. Clear the cell if we do not stock this product, or correct the SKU.`);
      }
    }

    // ── The numbers. Both required by the template contract; a price with no
    // pack size cannot be turned into a cost per unit, which is the number
    // every other screen reads.
    if (supplied.vendor_price === undefined && !rejected.has('vendor_price')) {
      entry.errors.push('Every row needs a price.');
    }
    if (supplied.pack_qty === undefined && !rejected.has('pack_qty')) {
      entry.errors.push('Every row needs how many units come in one pack. If the vendor sells it singly, enter 1.');
    }

    // ── The package type, after #468's mapping answers are applied.
    let packUnit;
    if (supplied.pack_unit !== undefined) {
      const answer = unitMap[norm(supplied.pack_unit)];
      if (answer === CREATE_UNIT) {
        packUnit = String(supplied.pack_unit).trim();
      } else if (answer !== undefined) {
        packUnit = answer === '' ? undefined : answer;
      } else if (knownUnits.has(norm(supplied.pack_unit))) {
        // Stored with the catalog's own capitalisation, not the spreadsheet's,
        // so the foreign key matches and the vendor page reads consistently.
        packUnit = (units.find(u => norm(u.name) === norm(supplied.pack_unit)) || {}).name;
      } else {
        entry.errors.push(`“${supplied.pack_unit}” is not one of our package types.`);
      }
    }

    // Named BEFORE the early exit. A row refused for a missing vendor or a
    // missing part number is exactly the row somebody has to go and find in the
    // spreadsheet, and "(no name)" beside a row number is the least helpful
    // thing this screen could say about it. Whatever the row does carry — the
    // description, failing that the part number, failing that the vendor as
    // typed — identifies it better than nothing.
    entry.displayName = supplied.off_catalog_description
      || (part !== undefined ? String(part).trim() : null)
      || supplied.vendor_id
      || null;

    if (!vendor || part === undefined) {
      entry.mode = 'error';
      plan.push(entry);
      continue;
    }

    // ── M3's within-file duplicate check. Two rows for the same product in one
    // file would apply two writes in an order nobody chose — reported rather
    // than resolved by row position.
    const pk = partKey(vendor.id, part);
    if (seenParts.has(pk)) {
      entry.errors.push(`${vendor.name} part ${part} also appears on row ${seenParts.get(pk)}. Remove one of them.`);
    } else {
      seenParts.set(pk, lineNo);
    }

    // ── Find the row this one is about, if Stede already has it. The part
    // number is tried first because it is the vendor's own identity for the
    // product; the item is the fallback for rows that predate part numbers.
    let target = byPart.get(pk) || null;
    if (!target && item) target = byItem.get(itemKey(vendor.id, item.id)) || null;

    const provisional = !target ? provisionalByPart.get(pk) : null;
    if (provisional) {
      entry.warnings.push(`${vendor.name} already has this part number from an off-catalog order. That record is left as it is and a catalog row is added alongside it.`);
    }

    // ── The part number is already attached to a DIFFERENT item. Refused
    // rather than re-pointed: one of the two is a typo, and guessing which
    // moves a price onto the wrong product.
    if (target && item && target.item_id != null && String(target.item_id) !== String(item.id)) {
      entry.errors.push(`${vendor.name} part ${part} is already linked to a different item in our catalog. Correct the SKU, or unlink it on the vendor’s page first.`);
    }

    // ── UNIQUE (vendor_id, item_id). Linking this row to an item the vendor
    // already has another row for would be refused by the database; caught here
    // so it reads as a sentence rather than as a constraint name.
    if (item) {
      const ik = itemKey(vendor.id, item.id);
      const other = byItem.get(ik);
      if (other && (!target || other.id !== target.id)) {
        entry.errors.push(`${vendor.name} already has a price for ${item.name} under part ${other.vendor_item_number || '(no part number)'}. One vendor can hold one price per item.`);
      }
      if (claimedItems.has(ik) && claimedItems.get(ik) !== lineNo) {
        entry.errors.push(`Row ${claimedItems.get(ik)} already links a part number to ${item.name} for this vendor. One vendor can hold one price per item.`);
      } else {
        claimedItems.set(ik, lineNo);
      }
    }

    // ── A contract price is not something an import may overwrite, and this
    // rule is NOT in the gate — it comes from the code. Every other way to
    // change a price in Stede refuses when the row is locked: the desktop row
    // editor and both mobile sheets all gate on `!isLocked`. Without this the
    // importer would be the only path in the whole application that can change
    // a contracted price, and it would do it four hundred rows at a time.
    if (target && target.price_locked) {
      entry.errors.push(`This price is locked under a contract. Unlock it on ${vendor.name}’s page before importing a new price for part ${part}.`);
    }

    // ── M10. Preferred means "reorder from here", and it is meaningless on a
    // row for something D&R does not stock — there is no item to prefer a
    // vendor FOR. Ignored there, and the preview says so rather than failing.
    let preferred;
    if (supplied.is_preferred !== undefined) {
      if (!item) {
        if (supplied.is_preferred === true) {
          entry.warnings.push('Preferred applies to something we stock, and this row is not linked to one of our items, so it is ignored.');
        }
      } else {
        preferred = supplied.is_preferred;
        if (preferred === true) {
          const key = String(item.id);
          if (seenPreferredItems.has(key)) {
            entry.errors.push(`Row ${seenPreferredItems.get(key)} also marks a preferred vendor for ${item.name}. Only one vendor can be preferred for an item — neither row is imported.`);
            const twin = plan.find(e => e.lineNo === seenPreferredItems.get(key));
            if (twin) {
              twin.errors.push(`Row ${lineNo} also marks a preferred vendor for ${item.name}. Only one vendor can be preferred for an item — neither row is imported.`);
              twin.mode = 'error';
            }
          } else {
            seenPreferredItems.set(key, lineNo);
            const holder = preferredByItem.get(key);
            if (holder && String(holder.vendor_id) !== String(vendor.id)) {
              // The same thing the Preferred toggle does on the vendor page:
              // one item has one preferred vendor, so setting it here clears
              // the other. Warned rather than done silently, because the vendor
              // losing it is not mentioned anywhere in the file.
              entry.warnings.push('This becomes the preferred vendor for that item, and whichever vendor holds it now stops being preferred.');
              entry.unpreferRowId = holder.id;
            }
          }
        }
      }
    }

    // ── Build the write. Only from what was supplied; a blank cell never
    // clears a stored value (D3, and it holds here for the same reason).
    //
    // `unit_cost` is NEVER written and cannot be: it is a GENERATED column,
    // vendor_price / pack_qty, computed by Postgres. Writing it is refused
    // rather than merely redundant.
    //
    // `is_provisional` is never written either — M18. It defaults false, which
    // is what an imported row is. Marking these provisional would put four
    // hundred unbought rows in front of the Off-Catalog Review queue.
    const values = {};
    if (supplied.vendor_price !== undefined) values.vendor_price = supplied.vendor_price;
    if (supplied.pack_qty !== undefined) values.pack_qty = supplied.pack_qty;
    if (packUnit !== undefined) values.pack_unit = packUnit;
    if (supplied.vendor_item_number !== undefined) values.vendor_item_number = String(part).trim();
    // The vendor's own wording for the product, kept whether or not the row
    // links to one of our items. On a vendor-only row it is the only name the
    // product has; on a linked row it is what the vendor calls it, which is
    // what appears on their invoice and is worth not throwing away.
    if (supplied.off_catalog_description !== undefined) {
      values.off_catalog_description = supplied.off_catalog_description;
    }
    if (preferred !== undefined) values.is_preferred = preferred;

    entry.vendorId = vendor.id;
    entry.vendorName = vendor.name;
    entry.partNumber = String(part).trim();
    entry.itemId = item ? item.id : null;
    entry.itemName = item ? item.name : null;
    entry.displayName = supplied.off_catalog_description
      || (item ? item.name : null)
      || String(part).trim();

    if (target) {
      values.item_id = item ? item.id : (target.item_id ?? null);
      for (const [key, v] of Object.entries(values)) {
        const before = target[key];
        const same = (before ?? null) === (v ?? null) || String(before ?? '') === String(v ?? '');
        if (!same) entry.changes.push({ key, label: LABEL[key] || key, before, after: v });
      }
      entry.mode = entry.changes.length ? 'update' : 'unchanged';
      entry.rowId = target.id;
      entry.linksNewItem = !!item && target.item_id == null;
    } else {
      entry.mode = 'create';
      values.vendor_id = vendor.id;
      values.item_id = item ? item.id : null;
    }

    // A price that changed gets today's date, matching every other price write
    // in the app. A row where nothing about the price moved keeps the date it
    // had, so "last priced" does not drift on a re-import that changed nothing.
    const priceMoved = entry.mode === 'create'
      || entry.changes.some(c => c.key === 'vendor_price' || c.key === 'pack_qty');
    if (priceMoved && values.vendor_price !== undefined) {
      values.price_date = new Date().toISOString();
    }

    entry.values = values;
    if (entry.errors.length) entry.mode = 'error';
    plan.push(entry);
  }

  return plan;
}

// The units the person chose to create during the mapping step, as the write
// step needs them: distinct, in the spreadsheet's own capitalisation.
export function unitsToCreate(questions, unitMap) {
  return (questions || [])
    .filter(q => unitMap[norm(q.value)] === CREATE_UNIT)
    .map(q => q.value);
}

// ── The write ────────────────────────────────────────────────────────────────
//
// Row by row rather than as one transaction, deliberately and for the same
// reason the vendor import is: a partial import is the intended behavior, so a
// failure part way through leaves the rows that succeeded in place and reports
// the rest. Locked rule 7 governs multi-table writes that must succeed
// TOGETHER; this is one table and must explicitly not be all-or-nothing.
//
// NEW UNITS ARE CREATED FIRST, ONCE, BEFORE ANY ROW. Two reasons, and both
// matter. `vendor_items.pack_unit` is a foreign key into `units_of_measure`, so
// a row referencing a unit that does not exist yet is refused by the database —
// every row using that unit would fail rather than the first one. And creating
// them here rather than when the person answers the question means abandoning
// the preview creates nothing: the catalog is only changed by pressing Import.
//
// No batch record is written. `log_vendor_item_changes()` fires per row and
// records who and when, and because this runs through the app as a signed-in
// user the `changed_by` failure that breaks a bulk load in the SQL editor
// cannot happen here.
export async function executeVendorItemsPlan(plan, { newUnits = [] } = {}, onProgress) {
  const result = { created: 0, updated: 0, unchanged: 0, skipped: 0, unitsAdded: 0, failed: [] };
  const writable = plan.filter(e => e.mode === 'create' || e.mode === 'update');

  result.unchanged = plan.filter(e => e.mode === 'unchanged').length;
  result.skipped = plan.filter(e => e.mode === 'error').length;

  for (const name of newUnits) {
    // 'package' rather than 'both': this column answers "how does the vendor
    // ship it", and a word that is also a stocking unit can be widened in
    // Company Settings by somebody who has looked at both lists. Guessing
    // 'both' here would put a packaging word into the stocking-unit picker on
    // every item screen.
    const { error } = await addUnit(name, 'package', null);
    if (error) {
      return {
        ...result,
        fatal: `Could not add the package type “${name}”: ${error.message || error}. Nothing was imported.`,
      };
    }
    result.unitsAdded += 1;
  }

  let done = 0;
  for (const entry of writable) {
    try {
      if (entry.unpreferRowId) {
        const { error } = await supabase
          .from('vendor_items').update({ is_preferred: false }).eq('id', entry.unpreferRowId);
        if (error) throw error;
      }
      if (entry.mode === 'create') {
        const { error } = await supabase.from('vendor_items').insert(entry.values);
        if (error) throw error;
        result.created += 1;
      } else {
        const patch = Object.fromEntries(entry.changes.map(c => [c.key, c.after]));
        if (entry.values.price_date) patch.price_date = entry.values.price_date;
        const { error } = await supabase
          .from('vendor_items').update(patch).eq('id', entry.rowId);
        if (error) throw error;
        result.updated += 1;
      }
    } catch (e) {
      result.failed.push({ lineNo: entry.lineNo, name: entry.displayName, message: e.message || String(e) });
    }
    done += 1;
    if (onProgress) onProgress(done, writable.length);
  }

  return result;
}
